import { useState, useEffect, useContext } from "react";
import ReactApexChart from "react-apexcharts";
import { ApexOptions } from "apexcharts";
import { API_BASE_URL } from "../../../environment";
import { AuthContext } from "../../../context/authcontext";

interface UsageChartProps {
  type: "api" | "webhook";
  startDate?: Date | null;
  endDate?: Date | null;
  apiClientId?: number | null;
}

interface UsageData {
  [key: string]: {
    success: number;
    failure: number;
  };
}

const UsageChart: React.FC<UsageChartProps> = ({
  type,
  startDate,
  endDate,
  apiClientId,
}) => {
  const { selectedTenantId } = useContext(AuthContext);
  const [interval, setInterval] = useState<"day" | "month">("day");
  const [loading, setLoading] = useState(false);
  const [chartData, setChartData] = useState<UsageData>({});

  useEffect(() => {
    if (selectedTenantId) {
      fetchUsageData();
    }
  }, [interval, type, selectedTenantId, startDate, endDate, apiClientId]);

  const fetchUsageData = async () => {
    setLoading(true);
    try {
      const endpoint =
        type === "api"
          ? `${API_BASE_URL}/api-audit-log-stats/`
          : `${API_BASE_URL}/api-webhook-delivery-stats/`;

      const params = new URLSearchParams();
      params.append("interval", interval);

      if (type === "api") {
        if (startDate)
          params.append(
            "request_datetime_gte",
            startDate.toISOString().split("T")[0]
          );
        if (endDate)
          params.append(
            "request_datetime_lte",
            endDate.toISOString().split("T")[0]
          );
      } else {
        if (startDate)
          params.append(
            "created_datetime_gte",
            startDate.toISOString().split("T")[0]
          );
        if (endDate)
          params.append(
            "created_datetime_lte",
            endDate.toISOString().split("T")[0]
          );
      }

      if (apiClientId) {
        params.append("api_client_id", apiClientId.toString());
      }

      const response = await fetch(`${endpoint}?${params.toString()}`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Basic ${localStorage.getItem("authCred")}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setChartData(data);
      }
    } catch (error) {
      console.error("Error fetching usage data:", error);
    } finally {
      setLoading(false);
    }
  };

  const getChartSeries = () => {
    const labels = Object.keys(chartData);
    const successData = labels.map((label) => chartData[label].success);
    const failureData = labels.map((label) => chartData[label].failure);

    return [
      {
        name: "Success",
        data: successData,
      },
      {
        name: "Failure",
        data: failureData,
      },
    ];
  };

  const getChartOptions = (): ApexOptions => {
    const labels = Object.keys(chartData);

    return {
      chart: {
        type: "bar",
        height: 350,
        stacked: true,
        toolbar: {
          show: true,
        },
        zoom: {
          enabled: true,
        },
      },
      responsive: [
        {
          breakpoint: 480,
          options: {
            legend: {
              position: "bottom",
              offsetX: -10,
              offsetY: 0,
            },
          },
        },
      ],
      plotOptions: {
        bar: {
          horizontal: false,
          borderRadius: 4,
          columnWidth: "60%",
        },
      },
      xaxis: {
        categories: labels,
        labels: {
          rotate: -45,
          rotateAlways: interval === "day",
          style: {
            fontSize: "10px",
          },
        },
      },
      yaxis: {
        title: {
          text: "Number of Requests",
        },
        labels: {
          formatter: function (val) {
            return Math.floor(val).toString();
          },
        },
      },
      legend: {
        position: "top",
        horizontalAlign: "left",
        offsetX: 40,
        markers: {
          shape: "square",
        },
      },
      fill: {
        opacity: 1,
      },
      colors: ["#28a745", "#dc3545"],
      dataLabels: {
        enabled: false,
      },
      tooltip: {
        y: {
          formatter: function (val) {
            return val + " requests";
          },
        },
      },
    };
  };

  const handleToggleChange = () => {
    setInterval((prev) => (prev === "day" ? "month" : "day"));
  };

  return (
    <>
      <style>
        {`
          .usage-chart-toggle-wrapper {
            position: relative;
            display: inline-flex;
            align-items: center;
            gap: 15px;
          }

          .usage-chart-toggle-wrapper .toggle-label {
            font-size: 14px;
            font-weight: 600;
            color: #333;
            min-width: 45px;
          }

          .usage-chart-toggle-wrapper .toggle-label.active {
            color: #824EEB;
          }

          .switch {
            position: relative;
            height: 1.5rem;
            width: 3rem;
            cursor: pointer;
            appearance: none;
            -webkit-appearance: none;
            border-radius: 9999px;
            background-color: rgba(100, 116, 139, 0.377);
            transition: all .3s ease;
          }

          .switch:checked {
            background-color: #824EEB;
          }

          .switch::before {
            position: absolute;
            content: "";
            left: calc(1.5rem - 1.6rem);
            top: calc(1.5rem - 1.6rem);
            display: block;
            height: 1.6rem;
            width: 1.6rem;
            cursor: pointer;
            border: 1px solid rgba(100, 116, 139, 0.527);
            border-radius: 9999px;
            background-color: rgba(255, 255, 255, 1);
            box-shadow: 0 3px 10px rgba(100, 116, 139, 0.327);
            transition: all .3s ease;
          }

          .switch:hover::before {
            box-shadow: 0 0 0px 8px rgba(0, 0, 0, .15);
          }

          .switch:checked:hover::before {
            box-shadow: 0 0 0px 8px rgba(130, 78, 235, .15);
          }

          .switch:checked:before {
            transform: translateX(100%);
            border-color: #824EEB;
          }

          /* Style ApexCharts legend markers with border radius */
          .apexcharts-legend-marker {
            border-radius: 4px !important;
          }
        `}
      </style>
      <div className="card">
        <div className="card-header d-flex justify-content-between align-items-center">
          <h5 className="card-title mb-0">
            {type === "api" ? "API" : "Webhook"} Usage Chart
          </h5>
          <div className="usage-chart-toggle-wrapper">
            <span
              className={`toggle-label ${interval === "day" ? "active" : ""}`}
            >
              Day
            </span>
            <input
              type="checkbox"
              role="switch"
              className="switch"
              id={`toggle-${type}`}
              checked={interval === "month"}
              onChange={handleToggleChange}
            />
            <span
              className={`toggle-label ${interval === "month" ? "active" : ""}`}
            >
              Month
            </span>
          </div>
        </div>
        <div className="card-body">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
            </div>
          ) : Object.keys(chartData).length > 0 ? (
            <ReactApexChart
              options={getChartOptions()}
              series={getChartSeries()}
              type="bar"
              height={350}
            />
          ) : (
            <div className="text-center py-5">
              <p className="text-muted">No data available</p>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default UsageChart;
